import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/auth";

const Status = ({ job }) => {
  if (!job || job.state === "idle") return null;
  const tone = job.state === "error" ? "text-rose" : job.state === "done" ? "text-ink" : "text-ink/75";
  return (
    <div className="mt-4 text-sm" data-testid="rebuild-status">
      <p className={`${tone} flex items-start gap-2`}>
        {job.state === "running" && <RefreshCw className="h-4 w-4 mt-0.5 shrink-0 animate-spin" />}
        <span data-testid="rebuild-status-message">{job.message}</span>
      </p>
      {job.state === "done" && job.pdf_url && (
        <a href={job.pdf_url.split("/api/")[0] || "/"} target="_blank" rel="noopener noreferrer" className="underline mt-2 inline-block" data-testid="rebuild-result-link">See it on the live site</a>
      )}
    </div>
  );
};

// "Rebuild the magazine": lays out a new PDF from the live site's articles and puts it online (takes ~5 minutes).
export const RebuildIssue = ({ onDone }) => {
  const [items, setItems] = useState([]);
  const [available, setAvailable] = useState(true);
  const [f, setF] = useState({ cover_slug: "", label: "Issue 27 · Autumn 2026", theme: "New Rebels" });
  const [job, setJob] = useState(null);

  useEffect(() => {
    api.get("/admin/issue/live-articles").then(({ data }) => {
      setItems(Array.isArray(data?.items) ? data.items : []);
      setAvailable(data?.available !== false);
    }).catch(() => toast.error("Could not read the live site's articles"));
    api.get("/admin/issue/rebuild/status").then(({ data }) => {
      setJob(data);
      if (data?.cover_slug) setF((s) => ({ ...s, cover_slug: data.cover_slug }));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (job?.state !== "running") return undefined;
    const t = setInterval(() => {
      api.get("/admin/issue/rebuild/status").then(({ data }) => {
        setJob(data);
        if (data?.state === "done") { toast.success("The new magazine is online"); onDone?.(); }
      }).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, [job?.state, onDone]);

  const start = async () => {
    if (!f.cover_slug) return toast.error("Choose the cover story");
    try {
      await api.post("/admin/issue/rebuild", f);
      setJob({ state: "running", message: "Reading the articles of the live site" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start");
    }
  };

  const running = job?.state === "running";
  return (
    <section className="border-2 border-ink p-5 sm:p-6 bg-paper" data-testid="rebuild-issue">
      <h2 className="display text-3xl sm:text-4xl">Rebuild the magazine</h2>
      <p className="text-sm text-ink/75 mt-2">Lays out a new printable PDF with every published article of ymagazine.com, then puts it online on the home page (download + flip-through). It takes about 5 minutes; you can leave this page.</p>
      {!available && <p className="text-sm text-rose mt-3" data-testid="rebuild-unavailable">Use this button from the preview panel: running it here would slow down the live site.</p>}
      <div className="grid gap-5 mt-5">
        <label className="block">
          <span className="kicker">Cover story</span>
          <select value={f.cover_slug} onChange={(e) => setF((s) => ({ ...s, cover_slug: e.target.value }))} className="admin-input mt-2" data-testid="rebuild-cover-select">
            <option value="">Choose an article…</option>
            {items.map((a) => <option key={a.slug} value={a.slug}>{a.category} — {a.title}</option>)}
          </select>
        </label>
        <div className="grid sm:grid-cols-2 gap-5">
          <label className="block">
            <span className="kicker">Issue line</span>
            <input value={f.label} onChange={(e) => setF((s) => ({ ...s, label: e.target.value }))} className="admin-input mt-2" data-testid="rebuild-label-input" />
          </label>
          <label className="block">
            <span className="kicker">Theme</span>
            <input value={f.theme} onChange={(e) => setF((s) => ({ ...s, theme: e.target.value }))} className="admin-input mt-2" data-testid="rebuild-theme-input" />
          </label>
        </div>
      </div>
      <button type="button" onClick={start} disabled={running || !available} className="mt-6 inline-flex items-center gap-2 kicker px-5 py-3 bg-ink text-cream hover:bg-hot hover:text-ink transition-colors disabled:opacity-60" data-testid="rebuild-start-btn">
        <RefreshCw className={`h-4 w-4 ${running ? "animate-spin" : ""}`} /> {running ? "Rebuilding…" : "Rebuild and publish"}
      </button>
      <Status job={job} />
    </section>
  );
};
